import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { subscriptionsApi } from '@api/endpoints/subscriptions'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import type {
  SubscriptionPlanDto,
  AdminSubscriptionTerm,
  AdminUserSubscriptionDto,
} from '@api/types/subscription'

interface ManageSubscriptionDialogProps {
  open: boolean
  onClose: () => void
  userId: string
  userLabel: string
  /** The subscription as currently loaded by the drawer, used to preselect the form. */
  current: AdminUserSubscriptionDto | null
  onSuccess: (updated: AdminUserSubscriptionDto) => void
}

const MONTH_PRESETS = [1, 3, 6, 12]

export default function ManageSubscriptionDialog({
  open,
  onClose,
  userId,
  userLabel,
  current,
  onSuccess,
}: ManageSubscriptionDialogProps) {
  const mobileDialogProps = useMobileDialogProps()
  const [plans, setPlans] = useState<SubscriptionPlanDto[]>([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [planId, setPlanId] = useState('')
  const [term, setTerm] = useState<AdminSubscriptionTerm>('Months')
  // Three long option labels side by side are ~420px; on a phone they stack instead of overflowing.
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))
  const [months, setMonths] = useState(3)
  const [endsOn, setEndsOn] = useState<Date | null>(null)
  const [status, setStatus] = useState<'Active' | 'Trial'>('Active')
  const [autoRenew, setAutoRenew] = useState(false)
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!open) return

    // Preselect the plan the user is already on so "extend by 3 months" is two clicks.
    setPlanId(current?.subscription?.plan.id ?? '')
    setTerm('Months')
    setMonths(3)
    setEndsOn(null)
    setStatus('Active')
    setAutoRenew(false)
    setReason('')
    setError(null)

    setPlansLoading(true)
    subscriptionsApi
      .getPlans()
      .then(data => setPlans(data || []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false))
  }, [open, current])

  const selectedPlan = useMemo(() => plans.find(p => p.id === planId), [plans, planId])
  const isFreePlan = selectedPlan?.isFreeTier === true

  // The backend ignores the term for the free plan — it is a downgrade, not a grant — so hide the
  // term controls rather than showing settings that will not be applied.
  const showTerm = !!selectedPlan && !isFreePlan

  const projectedEnd = useMemo(() => {
    if (!showTerm) return null
    if (term === 'NoExpiry') return 'never'
    if (term === 'EndsOn') return endsOn ? endsOn.toLocaleDateString() : null
    const d = new Date()
    d.setMonth(d.getMonth() + months)
    return d.toLocaleDateString()
  }, [showTerm, term, months, endsOn])

  const isValid =
    !!planId &&
    reason.trim().length > 0 &&
    (!showTerm ||
      (term === 'Months' && months >= 1 && months <= 120) ||
      (term === 'EndsOn' && !!endsOn && endsOn.getTime() > Date.now()) ||
      term === 'NoExpiry')

  const handleSubmit = async () => {
    if (!isValid) return
    setSubmitting(true)
    setError(null)
    try {
      const updated = await subscriptionsApi.setUserSubscription(userId, {
        subscriptionPlanId: planId,
        term: isFreePlan ? 'Months' : term,
        durationMonths: !isFreePlan && term === 'Months' ? months : null,
        endsOn: !isFreePlan && term === 'EndsOn' && endsOn ? endsOn.toISOString() : null,
        status,
        billingInterval: 'Monthly',
        autoRenew,
        reason: reason.trim(),
      })
      onSuccess(updated)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update subscription')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>Change Subscription</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Typography variant="body2" color="text.secondary">
            {userLabel}
          </Typography>

          <FormControl fullWidth required>
            <InputLabel>Plan</InputLabel>
            <Select
              value={planId}
              label="Plan"
              onChange={e => setPlanId(e.target.value)}
              disabled={plansLoading}
            >
              {plansLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading plans...
                </MenuItem>
              ) : (
                plans.map(plan => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.displayName}
                    {plan.isFreeTier ? ' (free tier)' : ` (${plan.formattedMonthlyPrice}/mo)`}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {isFreePlan && (
            <Alert severity="info">
              This downgrades the user to the free tier immediately. Paid capabilities stop at once.
            </Alert>
          )}

          {showTerm && (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  How long
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={term}
                  onChange={(_, v) => v && setTerm(v as AdminSubscriptionTerm)}
                  orientation={isPhone ? 'vertical' : 'horizontal'}
                  fullWidth={isPhone}
                  sx={{ mb: 1 }}
                >
                  <ToggleButton value="Months">For a number of months</ToggleButton>
                  <ToggleButton value="EndsOn">Until a date</ToggleButton>
                  <ToggleButton value="NoExpiry">No expiry</ToggleButton>
                </ToggleButtonGroup>

                {term === 'Months' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {MONTH_PRESETS.map(m => (
                      <Button
                        key={m}
                        size="small"
                        variant={months === m ? 'contained' : 'outlined'}
                        onClick={() => setMonths(m)}
                      >
                        {m} mo
                      </Button>
                    ))}
                    <TextField
                      label="Months"
                      type="number"
                      size="small"
                      value={months}
                      onChange={e =>
                        setMonths(Math.max(1, Math.min(120, parseInt(e.target.value) || 1)))
                      }
                      inputProps={{ min: 1, max: 120 }}
                      sx={{ width: 110 }}
                    />
                  </Box>
                )}

                {term === 'EndsOn' && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Ends on"
                      value={endsOn}
                      onChange={setEndsOn}
                      disablePast
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </LocalizationProvider>
                )}

                {term === 'NoExpiry' && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    This subscription will never lapse on its own. Use it for staff and permanent
                    comps only — everything else should carry a term.
                  </Alert>
                )}

                {projectedEnd && term !== 'NoExpiry' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Reverts to the free tier on {projectedEnd}.
                  </Typography>
                )}
              </Box>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={e => setStatus(e.target.value as 'Active' | 'Trial')}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Trial">Trial</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={<Switch checked={autoRenew} onChange={e => setAutoRenew(e.target.checked)} />}
                label="Auto-renew"
              />
              {autoRenew && (
                <Alert severity="warning">
                  Nothing renews this automatically — there is no payment processor wired up. Leaving
                  auto-renew on only stops the term from reading as a deliberate comp.
                </Alert>
              )}
            </>
          )}

          <TextField
            label="Reason"
            required
            fullWidth
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Support compensation, contest prize, staff account"
            helperText="Recorded in this user's audit history"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid || submitting}>
          {submitting ? 'Saving...' : isFreePlan ? 'Downgrade to Free' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
